'use client';
import React, { useState } from 'react';
import { CheckCircle2, Zap, RotateCcw, Phone, ChevronDown, Eye } from 'lucide-react';
import LeverageBadge from '@/components/ui/LeverageBadge';
import { EstimateResult, QuoteFormData } from './QuickQuoteWizard';

interface EstimateRevealProps {
  estimate: EstimateResult;
  formData: QuoteFormData;
  onNewQuote: () => void;
}

export default function EstimateReveal({ estimate, formData, onNewQuote }: EstimateRevealProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const leverageColor = estimate.leverageScore === 'High' ?'from-amber-500/20 to-transparent border-amber-500/30'
    : estimate.leverageScore === 'Medium' ?'from-blue-500/20 to-transparent border-blue-500/30' :'from-zinc-700/20 to-transparent border-zinc-700/30';

  // Vision analysis from the estimate result (if available)
  const vision = (estimate as any).visionAnalysis as {
    estimatedSqft: number | null;
    conditionNotes: string;
    scopeComplexity: 'simple' | 'moderate' | 'complex';
    confidenceBoost: number;
    photoQuality: 'good' | 'acceptable' | 'missing';
  } | undefined;

  const complexityColor =
    vision?.scopeComplexity === 'complex'  ? 'text-red-400 bg-red-500/10 border-red-500/20' :
    vision?.scopeComplexity === 'moderate'? 'text-amber-400 bg-amber-500/10 border-amber-500/20' : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="w-full border-b border-[#1A1A1A] bg-[#0D0D0D]">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <CheckCircle2 size={16} className="text-emerald-400" />
          </div>
          <span className="font-700 text-white text-sm">Estimate Ready</span>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-start px-4 py-8 pb-8">
        <div className="w-full max-w-lg space-y-5 animate-slide-up">

          {/* Main estimate card */}
          <div className={`leverage-card bg-gradient-to-b ${leverageColor} p-6 border`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[#888] text-xs font-500 uppercase tracking-widest mb-1">Rough Estimate</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-800 text-white tabular-nums">
                    ${estimate.roughEstimate.min.toLocaleString()}
                  </span>
                  <span className="text-xl text-[#666] font-500">–</span>
                  <span className="text-4xl font-800 text-white tabular-nums">
                    ${estimate.roughEstimate.max.toLocaleString()}
                  </span>
                </div>
                <p className="text-[#555] text-xs mt-1 font-500">
                  {estimate.confidence}% confidence · {estimate.projectType}
                </p>
              </div>
              <LeverageBadge level={estimate.leverageScore} />
            </div>

            {/* Score reason */}
            <div className="flex items-start gap-2.5 p-3 bg-black/30 rounded-lg border border-white/5">
              <Zap size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-[#AAA] text-xs leading-relaxed">{estimate.scoreReason}</p>
            </div>

            {/* Score factors pills */}
            {estimate.scoreFactors && (
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { label: 'Budget', value: estimate.scoreFactors.budgetSignal, colors: { strong: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25', moderate: 'bg-amber-500/15 text-amber-400 border-amber-500/25', weak: 'bg-zinc-700/30 text-zinc-400 border-zinc-600/25' } },
                  { label: 'Urgency', value: estimate.scoreFactors.urgencySignal, colors: { high: 'bg-red-500/15 text-red-400 border-red-500/25', medium: 'bg-amber-500/15 text-amber-400 border-amber-500/25', low: 'bg-zinc-700/30 text-zinc-400 border-zinc-600/25' } },
                  { label: 'Intent', value: estimate.scoreFactors.intentSignal, colors: { committed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25', conditional: 'bg-amber-500/15 text-amber-400 border-amber-500/25', browsing: 'bg-zinc-700/30 text-zinc-400 border-zinc-600/25' } },
                  { label: 'Photo', value: estimate.scoreFactors.photoQuality, colors: { good: 'bg-blue-500/15 text-blue-400 border-blue-500/25', acceptable: 'bg-amber-500/15 text-amber-400 border-amber-500/25', missing: 'bg-zinc-700/30 text-zinc-400 border-zinc-600/25' } },
                ].map(({ label, value, colors }) => {
                  const colorClass = (colors as Record<string, string>)[value] ?? 'bg-zinc-700/30 text-zinc-400 border-zinc-600/25';
                  return (
                    <span key={label} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-600 uppercase tracking-wide ${colorClass}`}>
                      {label}: {value}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* AI Vision Analysis card (shown only when vision data is present) */}
          {vision && (
            <div className="leverage-card p-5 border border-blue-500/20 bg-blue-500/5">
              <div className="flex items-center gap-2 mb-3">
                <Eye size={14} className="text-blue-400" />
                <span className="text-blue-300 text-xs font-600 uppercase tracking-wide">AI Photo Analysis</span>
              </div>
              <div className="space-y-3">
                {/* Condition notes */}
                <p className="text-[#AAA] text-xs leading-relaxed">{vision.conditionNotes}</p>

                <div className="flex flex-wrap gap-2">
                  {/* Scope complexity badge */}
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-600 uppercase tracking-wide ${complexityColor}`}>
                    {vision.scopeComplexity} scope
                  </span>

                  {/* Estimated sqft badge */}
                  {vision.estimatedSqft && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-blue-500/20 bg-blue-500/10 text-blue-300 text-[10px] font-600 uppercase tracking-wide">
                      ~{vision.estimatedSqft.toLocaleString()} sqft estimated
                    </span>
                  )}

                  {/* Confidence boost badge */}
                  {vision.confidenceBoost > 0 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 text-emerald-300 text-[10px] font-600 uppercase tracking-wide">
                      +{vision.confidenceBoost}% confidence from photo
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Calculation breakdown (collapsible) */}
          {estimate.breakdown && (
            <div className="leverage-card overflow-hidden">
              <button
                onClick={() => setShowBreakdown((v) => !v)}
                className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-white/[0.02] transition-colors"
              >
                <span className="text-[#888] text-xs font-600 uppercase tracking-wide">How we calculated this</span>
                <ChevronDown size={14} className={`text-[#555] transition-transform duration-200 ${showBreakdown ? 'rotate-180' : ''}`} />
              </button>
              {showBreakdown && (
                <div className="px-5 pb-4 space-y-2 border-t border-[#1A1A1A]">
                  <div className="pt-3 space-y-2">
                    {[
                      { label: 'Applied Rule', value: estimate.breakdown.appliedRule },
                      { label: 'Base Fee', value: `$${estimate.breakdown.baseFee.toLocaleString()}` },
                      { label: `Per ${estimate.breakdown.unit} Rate`, value: `$${estimate.breakdown.perUnitRate}/${estimate.breakdown.unit}` },
                      { label: `Estimated ${estimate.breakdown.unit}s`, value: `~${estimate.breakdown.estimatedUnits.toLocaleString()} ${estimate.breakdown.unit}` },
                      { label: 'Subtotal', value: `$${estimate.breakdown.subtotal.toLocaleString()}` },
                      { label: '±20% Variance', value: `$${estimate.breakdown.variance.toLocaleString()}` },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between">
                        <span className="text-[#555] text-xs">{label}</span>
                        <span className="text-[#999] text-xs font-500 font-mono">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="leverage-card p-5 space-y-3">
            <h3 className="text-white font-600 text-sm">Quote Summary</h3>
            <div className="space-y-2.5">
              {[
                { label: 'Project Type', value: formData.projectType },
                { label: 'Budget Range', value: formData.budgetRange },
                { label: 'Timeline', value: formData.timeline },
                { label: 'Priority', value: formData.priority },
                { label: 'Contact', value: `${formData.firstName} ${formData.lastName}` },
              ].map(({ label, value }) => (
                <div key={`summary-${label}`} className="flex items-center justify-between">
                  <span className="text-[#666] text-xs">{label}</span>
                  <span className="text-[#CCC] text-xs font-500 text-right max-w-[55%] truncate">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* What happens next */}
          <div className="leverage-card p-5">
            <h3 className="text-white font-600 text-sm mb-3">What happens next</h3>
            <div className="space-y-3">
              {[
                { step: '1', text: 'A professional reviews your photo within 2 hours', icon: '📷' },
                { step: '2', text: 'You receive a detailed quote via email and text', icon: '📧' },
                { step: '3', text: 'Schedule your project at a time that works for you', icon: '📅' },
              ].map(({ step, text, icon }) => (
                <div key={`next-${step}`} className="flex items-start gap-3">
                  <span className="text-lg flex-shrink-0">{icon}</span>
                  <p className="text-[#888] text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex gap-3">
            <a
              href="tel:+19027071875"
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-600 rounded-lg py-4 transition-all duration-150"
            >
              <Phone size={18} />
              Call Now
            </a>
            <button
              onClick={onNewQuote}
              className="leverage-btn-secondary flex items-center gap-2 px-5 py-4"
            >
              <RotateCcw size={16} />
              New Quote
            </button>
          </div>

          {/* Disclaimer */}
          <p className="text-[11px] text-[#444] text-center leading-relaxed">
            This is a rough AI-generated estimate. Final pricing depends on on-site inspection and project scope. Not a binding quote.
          </p>
        </div>
      </div>

      <footer className="py-4 text-center border-t border-[#1A1A1A]">
        <p className="text-[11px] text-[#444] font-500 tracking-wide">
          Powered by{' '}
          <a href="https://quotes.seriousleverage.com" target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-blue-400 transition-colors">
            Serious Quotes
          </a>
          {' '}— AI Lead Qualification
        </p>
      </footer>
    </div>
  );
}